import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get, sum } from 'lodash';

import { Paper } from '@mui/material';

import { useSelector } from '../../store/utils';

import { Battery } from '../../atoms/icons/battery';
import { SolarPanel } from '../../atoms/icons/solar-panel';
import { ElectricalGrid } from '../../atoms/icons/electrical-grid';
import { Boiler } from '../../atoms/icons/boiler';
import { House } from '../../atoms/icons/house';
import { HeatPump } from '../../atoms/icons/heat-pump';
import { Car } from '../../atoms/icons/car';
import { Wind } from '../../atoms/icons/wind';

import { AnimationItem, ItemOptions } from './animation.item';
import { AnimationWrapper } from './animation.wrapper';

export const Animation = () => {
  const [
    consumptionPhase1,
    consumptionPhase2,
    consumptionPhase3,
    pvActualPower,
    hwActualPower,
    pvActualPowerBattery,
    homeActualPower,
    hpActualPower,
    windPower,
    hpEnabled,
    pvInstalled,
    pvBattery,
    hwInstalled,
    wbCarConnect,
    wbActualPower,
    emInstalled,
    windInstalled,
  ] = useSelector(
    (s) =>
      [
        get(s.variables, 'anim_EM_Consumption[0]'),
        get(s.variables, 'anim_EM_Consumption[1]'),
        get(s.variables, 'anim_EM_Consumption[2]'),
        get(s.variables, 'anim_PV_ACTUAL_POWER'),
        get(s.variables, 'anim_HW_ACTUAL_POWER'),
        get(s.variables, 'anim_PV_ACTUAL_POWER_BATTERY'),
        get(s.variables, 'anim_HOME_ACTUAL_POWER'),
        get(s.variables, 'anim_HP_ACTUAL_POWER'),
        get(s.variables, 'anim_WIND_POWER'),
        get(s.variables, 'HP_ENABLED'),
        get(s.variables, 'PV_INSTALL'),
        get(s.variables, 'PV_BATTERY'),
        get(s.variables, 'HW_INSTALL'),
        get(s.variables, 'WB_CAR_CONNECT'),
        get(s.variables, 'anim_WB_ACTUAL_POWER'),
        get(s.variables, 'EM_INSTALL'),
        get(s.variables, 'WIND_INSTALL'),
      ] as const
  );

  const consumption = sum([
    consumptionPhase1,
    consumptionPhase2,
    consumptionPhase3,
  ]);

  const items: ItemOptions[] = [
    {
      label: (
        <FormattedMessage id="animation.photovoltaics" defaultMessage="FVE" />
      ),
      icon: <SolarPanel sx={{ mx: -0.75, mt: -1 }} />,
      value: pvActualPower,
      hidden: !pvInstalled,
    },
    {
      label: <FormattedMessage id="animation.boiler" defaultMessage="Bojler" />,
      icon: <Boiler whiteBg sx={{ mx: -1.5, mt: -1 }} />,
      value: hwActualPower,
      hidden: !hwInstalled,
      valueIsConsumption: true,
    },
    {
      label: (
        <FormattedMessage id="animation.battery" defaultMessage="Baterie" />
      ),
      icon: <Battery sx={{ mx: -0.5, mt: -1 }} />,
      value: pvActualPowerBattery,
      hidden: !pvBattery,
    },
    {
      label: (
        <FormattedMessage id="animation.electrical_grid" defaultMessage="Síť" />
      ),
      icon: <ElectricalGrid sx={{ mx: -1.5, mt: -1 }} />,
      value: consumption,
      hidden: !emInstalled,
    },
    {
      label: (
        <FormattedMessage id="animation.consumption" defaultMessage="Dům" />
      ),
      icon: <House sx={{ mx: -1.5, mt: -1 }} />,
      value: homeActualPower,
      valueIsConsumption: true,
      hidden: !emInstalled,
    },
    {
      label: <FormattedMessage id="animation.hp" defaultMessage="Tepelko" />,
      icon: <HeatPump sx={{ mx: -1.5, mt: -1, width: 30 }} />,
      value: hpActualPower,
      valueIsConsumption: true,
      hidden: !hpEnabled,
    },
    {
      label: (
        <FormattedMessage id="animation.wallbox" defaultMessage="Wallbox" />
      ),
      icon: <Car sx={{ mx: 0, mt: 0, width: 24 }} />,
      value: wbActualPower,
      valueIsConsumption: true,
      hidden: !wbCarConnect,
    },
    {
      label: <FormattedMessage id="animation.wind" defaultMessage="Vítr" />,
      icon: <Wind sx={{ mx: -1.5, mt: -1, width: 30 }} />,
      value: windPower,
      hidden: !windInstalled,
    },
  ];

  return (
    <Paper sx={{ px: 0.25 }}>
      <AnimationWrapper>
        {items
          .filter(({ hidden }) => !hidden)
          .map((item, index, array) => {
            const rotate = (360 / array.length) * index;

            return <AnimationItem rotate={rotate} key={index} {...item} />;
          })}
      </AnimationWrapper>
    </Paper>
  );
};
